'use client';

/**
 * ItalicBody — the Fraunces italic body copy used as form-field
 * hints, captions under inputs, project descriptions, and tooltip-y
 * footnotes throughout the app.
 *
 * Two sizes:
 *   sm (default) — 12px / lineHeight 1.45 / color var(--ink-2).
 *                  The hint recipe — sits under inputs and pickers
 *                  ("This field is locked once the project is
 *                  finished.", "Hex color is optional.", etc.).
 *   md           — 14px / lineHeight 1.5 / color var(--ink).
 *                  The body recipe — long-form prose like the
 *                  technique description, tips, project notes,
 *                  yarn review.
 *
 * Default render is a `<p>` with `margin: 0` (caller controls
 * spacing). Pass `as="span"` for inline rendering inside another
 * block.
 *
 * Replaces ~40 hand-rolled Fraunces-italic sites across ProjectForm,
 * TechniqueForm, projects/view, techniques, inspiration, yarn, etc.
 */

import type { CSSProperties, ReactNode } from 'react';

export type ItalicBodySize = 'sm' | 'md';

export type ItalicBodyProps = {
  children: ReactNode;
  size?: ItalicBodySize;
  /** Render as `<p>` (default) or `<span>` for inline use. */
  as?: 'p' | 'span';
  /** Per-instance overrides — margin, alignment, etc. */
  style?: CSSProperties;
  className?: string;
  /**
   * Passed through to the rendered element. Declared explicitly because
   * TypeScript does NOT check hyphenated JSX attributes on components: an
   * undeclared `data-testid` is dropped silently, and the test that looks for
   * it fails with a locator that can never match. (Two sites in the charts
   * editor were already passing one into the void before this existed.)
   */
  'data-testid'?: string;
};

const SIZE_STYLES: Record<ItalicBodySize, CSSProperties> = {
  sm: { fontSize: 12, lineHeight: 1.45, color: 'var(--ink-2)' },
  md: { fontSize: 14, lineHeight: 1.5, color: 'var(--ink)' },
};

export function ItalicBody({
  children,
  size = 'sm',
  as = 'p',
  style,
  className,
  'data-testid': testId,
}: ItalicBodyProps) {
  const baseStyle: CSSProperties = {
    margin: 0,
    fontFamily: "'Fraunces', 'Times New Roman', serif",
    fontStyle: 'italic',
    fontWeight: 400,
    ...SIZE_STYLES[size],
    ...style,
  };

  if (as === 'span') {
    return (
      <span className={className} style={baseStyle} data-testid={testId}>
        {children}
      </span>
    );
  }
  return (
    <p className={className} style={baseStyle} data-testid={testId}>
      {children}
    </p>
  );
}
